import log from 'loglevel';
import { watch } from 'vue';
import { defineStore } from 'pinia';

import _ from 'lodash';

import { useUserStore } from './UserStore';
import { useAuthStore } from './AuthStore';
import { InHouseTasksService } from '../api/services/InHouseTasksService';


import { Paging } from '@/interfaces/Paging';
import { EarnData } from '@/interfaces/InHouseTasks/EarnData';
import { InHouseTaskNames } from '../constants/InHouseTaskNames';
import { VerifyOTPParams } from '@/interfaces/InHouseTasks/VerifyOTPParams';
import { EarnDataAPIResponse } from '@/interfaces/InHouseTasks/EarnDataAPIResponse';
import { InHouseTasks, createInHouseTask } from '@/interfaces/InHouseTasks/InHouseTasks';
import { VerifyPhoneNumberInput } from '@/interfaces/InHouseTasks/VerifyPhoneNumberInput';
import { ResendOTPResponse } from '@/interfaces/InHouseTasks/ResendOTPResponse';
import { VerifyPhoneNumberResponse } from '@/interfaces/InHouseTasks/VerifyPhoneNumberResponse';


export const UserActivityStore = defineStore('inHouseTasks', {
	state: () => ({
		inHouseTasks: {} as Record<string, InHouseTasks>,
		loadingInHouseTasks: false,


		earnData: [] as EarnData[],
		earnDataPaging: {
			count: 25,
			offset: 0,
			page: 1,
			totalPages: 1,
		} as Paging,
		loadingEarnData: false,


		selectedEarnData: {} as EarnData,
		selectedInHouseTask: {} as InHouseTasks,

	}),




	actions: {




		async initialize() {
			console.log('Initializing UserActivityStore');
			try {
				// Only fetch data if user is logged in
				const authStore = useAuthStore();
				if (authStore.isUserLoggedIn()) {
					await this.getAllInHouseTasks();
					await this.getEarnData();
				} else {
					log.info("User not logged in, skipping user activity initialization");
				}
				
				this.setupWatchers();
				console.log('UserActivityStore initialized successfully');
				return true;
			} catch (error) {
				log.error("Failed to initialize UserActivityStore:", error);
				return false;
			}
		},




		async getAllInHouseTasks() {
			this.loadingInHouseTasks = true;
			try {
				this.inHouseTasks = await InHouseTasksService.getInHouseTasks();
			} catch (error) {
				log.error("Failed to get in-house tasks:", error);
			} finally {
				this.loadingInHouseTasks = false;
			}
		},




		async claimDailyCheckin() {
			try {
				const response = await InHouseTasksService.claimDailyCheckin();
				if (response?.earn_option?.name) {
					this.inHouseTasks[response.earn_option.name] = response.earn_option;
				}
				
				const userStore = useUserStore();
				if (response?.user) {
					userStore.syncUser(response.user);
				}
				return response;
			} catch (error) {
				log.error("Failed to claim daily check-in:", error);
				throw error;
			}
		},






		async verifyPhoneNumber(input: VerifyPhoneNumberInput): Promise<boolean> {
			try {
				const phoneNumberInputFromStore = this.getPhoneNumberDataFromStore();

				if (_.some(phoneNumberInputFromStore, _.isNil)) {
					return await this.resendOTP();
				}

				if (_.isEqual(input, phoneNumberInputFromStore)) {
					return await this.resendOTP();
				}

				this.updatePhoneNumberInStore(input);
				const response: VerifyPhoneNumberResponse = await InHouseTasksService.verifyPhoneNumber(input);
				
				const userStore = useUserStore();
				if (response?.user) {
					userStore.syncUser(response.user);
				}
				
				if (response?.user?.verification_status && this.inHouseTasks[InHouseTaskNames.VERIFY_PHONE]) {
					this.inHouseTasks[InHouseTaskNames.VERIFY_PHONE].status = response.user.verification_status;
				}
				
				return response?.sms_sent ?? false;
			} catch (error) {
				log.error("Failed to verify phone number:", error);
				throw error;
			}
		},







		async verifyOTP(input: VerifyOTPParams) {
			try {
				const response = await InHouseTasksService.verifyOTP(input);
				
				if (response?.success) {
					if (response?.user?.verification_status && this.inHouseTasks[InHouseTaskNames.VERIFY_PHONE]) {
						this.inHouseTasks[InHouseTaskNames.VERIFY_PHONE].status = response.user.verification_status;
					}
					this.removeSelectedInHouseTask();
				} else {
					throw new Error("Invalid OTP");
				}
				
				const userStore = useUserStore();
				if (response?.user) {
					userStore.syncUser(response.user);
				}
				
				return response;
			} catch (error) {
				log.error("Failed to verify OTP:", error);
				throw error;
			}
		},




		async resendOTP(): Promise<boolean> {
			try {
				const response: ResendOTPResponse = await InHouseTasksService.resendOTP();
				return response?.success ?? false;
			} catch (error) {
				log.error("Failed to resend OTP:", error);
				throw error;
			}
		},




		async getEarnData() {
			this.loadingEarnData = true;
			try {
				const response: EarnDataAPIResponse = await InHouseTasksService.getEarnData(this.earnDataPaging);
				
				// Use optional chaining and nullish coalescing for clean handling
				this.earnData = response?.data ?? [];
				this.earnDataPaging.totalPages = response?.paging?.totalPages ?? 1;
			} catch (error) {
				log.error("Failed to get in-house tasks:", error);
			} finally {
				this.loadingEarnData = false;
			}
		},



		setupWatchers() {
			watch(() => this.earnDataPaging.page, (newValue: number) => {
				this.earnDataPaging.offset = (newValue - 1) * this.earnDataPaging.count;
				
				// Only fetch data if user is logged in
				const authStore = useAuthStore();
				if (authStore.isUserLoggedIn()) {
					this.getEarnData();
				}
			});
		},




		async redeemCoinsForSelectedInHouseTask(params: any) {
			this.disableAvailabilityOfSelectedInHouseTask();
			let response;
			try {
				response = await InHouseTasksService.claimInHouseTaskById(this.selectedInHouseTask.id, params);
				
				const userStore = useUserStore();
				if (response?.user) {
					userStore.syncUser(response.user);
				}
				
				if (response?.earn_option) {
					this.syncInHouseTasksByName(response.earn_option);
				}
				
				return response;
			} catch (error) {
				this.enableAvailabilityOfSelectedInHouseTask();
				log.error("Failed to redeem coins:", error);
				throw error;
			}
		},



		getPhoneNumberDataFromStore(): VerifyPhoneNumberInput {
			const verifyPhone = this.inHouseTasks[InHouseTaskNames.VERIFY_PHONE] || {};
			return {
				phone_number: verifyPhone?.phone_number,
				phone_country_code: verifyPhone?.phone_country_code,
				phone_iso_code: verifyPhone?.phone_iso_code,
			};
		},



		updatePhoneNumberInStore(input?: VerifyPhoneNumberInput): VerifyPhoneNumberInput {
			const verifyPhone = this.inHouseTasks[InHouseTaskNames.VERIFY_PHONE];
			
			if (!verifyPhone) {
				return input || {} as VerifyPhoneNumberInput;
			}
			
			if (!input || !input.phone_number) {
				input = {
					phone_number: verifyPhone.phoneNumber,
					phone_country_code: verifyPhone.phone_country_code,
					phone_iso_code: verifyPhone.phone_iso_code,
				};
			}

			verifyPhone.phoneNumber = input.phone_number;
			verifyPhone.phone_country_code = input.phone_country_code;
			verifyPhone.phone_iso_code = input.phone_iso_code;

			return input;
		},



		getEarnDataById(id: number) {
			// earn data is an array of objects and the objects contain that id that we are looking for
			return this.earnData.find((earnData) => earnData.id === id);
		},


		setSelectedEarnData(myEarnDataId: number) {
			const optionToSet = this.earnData.find((option) => option.id === myEarnDataId);
			if(optionToSet){
				this.selectedEarnData = optionToSet;
			}
		},


		selectInHouseTaskByName(name: string) {
			if (this.inHouseTasks[name]) {
				this.selectedInHouseTask = { ...this.inHouseTasks[name] };
			}
		},




		syncInHouseTasksByName(inHouseTask: InHouseTasks) {
			if (inHouseTask?.name && this.inHouseTasks[inHouseTask.name]) {
				_.merge(this.inHouseTasks[inHouseTask.name], createInHouseTask(inHouseTask));
			}
		},




		disableAvailabilityOfSelectedInHouseTask() {
			if (this.selectedInHouseTask) {
				this.selectedInHouseTask.available = false;
			}
		},



		enableAvailabilityOfSelectedInHouseTask() {
			if (this.selectedInHouseTask) {
				this.selectedInHouseTask.available = true;
			}
		},



		removeSelectedInHouseTask() {
			this.selectedInHouseTask = {} as InHouseTasks;
		},



		setPage(page: number) {
			this.earnDataPaging.page = page;
		},




		clearStoreData() {
			this.$reset();
		},




	}
});
